import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  USER_ROLES,
  canManageRole,
  passwordSchema,
  userCreateSchema,
  userUpdateSchema,
  type User,
  type UserRole,
} from '@afiliados/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { usersApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { useAuthStore } from '@/features/auth/store';
import { PasswordInput } from './PasswordInput';

interface Draft {
  name: string;
  email: string;
  role: UserRole;
  password: string;
  mustChangePassword: boolean;
  active: boolean;
}
const EMPTY: Draft = {
  name: '',
  email: '',
  role: 'operator',
  password: '',
  mustChangePassword: true,
  active: true,
};

export function UserFormDialog({
  open,
  onOpenChange,
  user,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Sem `user`: criação. */
  user?: User;
}) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const isSelf = !!user && user.id === me?.id;
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setDraft(
      user
        ? { ...EMPTY, name: user.name, email: user.email, role: user.role, active: user.active }
        : EMPTY,
    );
  }, [open, user]);

  // Só se atribui perfil que o próprio ator alcança (a API valida de novo).
  const roles = USER_ROLES.filter((r) => !!me && canManageRole(me.role, r));

  const mut = useMutation({
    mutationFn: async (d: Draft) => {
      if (user) {
        const body = userUpdateSchema.parse({
          name: d.name,
          email: d.email,
          role: d.role,
          ...(isSelf ? {} : { active: d.active }),
        });
        return usersApi.update(user.id, body);
      }
      const body = userCreateSchema.parse({
        name: d.name,
        email: d.email,
        role: d.role,
        password: d.password,
        mustChangePassword: d.mustChangePassword,
      });
      return usersApi.create(body);
    },
    onSuccess: () => {
      toast.success(user ? 'Usuário atualizado' : 'Usuário criado');
      void qc.invalidateQueries({ queryKey: ['users'] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao salvar'),
  });

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (draft.name.trim().length < 2) next.name = 'Informe o nome';
    if (!/^\S+@\S+\.\S+$/.test(draft.email.trim())) next.email = 'E-mail inválido';
    if (!user) {
      const pw = passwordSchema.safeParse(draft.password);
      if (!pw.success) next.password = pw.error.issues[0]?.message ?? 'Senha inválida';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{user ? 'Editar usuário' : 'Novo usuário'}</DialogTitle>
          <DialogDescription>
            {user
              ? 'Alterar o perfil ou desativar encerra as sessões abertas do usuário.'
              : 'Entregue a senha temporária ao usuário; ele define a própria no primeiro acesso.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (validate()) mut.mutate(draft);
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="user-name">Nome</Label>
            <Input
              id="user-name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Maria Souza"
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="user-email">E-mail</Label>
            <Input
              id="user-email"
              type="email"
              autoComplete="off"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              placeholder="maria@empresa.com"
            />
            {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="user-role">Perfil</Label>
            <Select
              value={draft.role}
              onValueChange={(v) => setDraft({ ...draft, role: v as UserRole })}
              disabled={isSelf}
            >
              <SelectTrigger id="user-role" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[draft.role]}</p>
          </div>
          {!user && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="user-password">Senha temporária</Label>
                <PasswordInput
                  id="user-password"
                  value={draft.password}
                  onChange={(password) => setDraft({ ...draft, password })}
                />
                {errors.password ? (
                  <p className="text-xs text-destructive">{errors.password}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Pelo menos 8 caracteres, com letra e número.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="user-must-change"
                  checked={draft.mustChangePassword}
                  onCheckedChange={(v) => setDraft({ ...draft, mustChangePassword: v })}
                />
                <Label htmlFor="user-must-change">Exigir troca de senha no primeiro acesso</Label>
              </div>
            </>
          )}
          {user && !isSelf && (
            <div className="flex items-center gap-2">
              <Switch
                id="user-active"
                checked={draft.active}
                onCheckedChange={(v) => setDraft({ ...draft, active: v })}
              />
              <Label htmlFor="user-active">Usuário ativo</Label>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending && <Loader2 className="animate-spin" />} Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
